/**
 * One list of everything you can do on a job (job page v2, slice 1;
 * threads/job-page-v2/mockup.html, the mockup Corbin approved 2026-10-08).
 *
 * `jobActions(ctx, handlers)` returns every job action with its label, icon,
 * group, whether it applies to THIS job (and why not, when it doesn't), and
 * what it runs. The ⋯ menu, ⌘K's "This job" group, the right-click menus on
 * device and file rows, and the pinned bar all read from this one list, so a
 * thing you can do in one of them you can do in all of them.
 *
 * Nothing here runs anything on its own. `handlers` comes from the page and
 * maps each id to the page's EXISTING handler (the same mutation, dialog or
 * link the classic page uses). The type makes that map exhaustive: add an
 * id here and the page will not compile until it says what that id runs.
 *
 * `jobActions.classic.ts` lists every action on the classic job page and
 * `jobActions.test.ts` fails if any one of them has no action here.
 *
 * Only the new job page (the per-user "Try the new job page" switch) reads
 * this. The classic page is unchanged.
 */
import type { LucideIcon } from "lucide-react";
import {
    Ban,
    BadgeCheck,
    Building2,
    Calculator,
    Calendar,
    CalendarPlus,
    Camera,
    CheckCircle2,
    ClipboardCheck,
    ClipboardList,
    Copy,
    DollarSign,
    Eye,
    Download,
    ExternalLink,
    FileCheck2,
    FileText,
    FileX2,
    Flag,
    FolderOpen,
    Gauge,
    Hash,
    HelpCircle,
    History,
    Hourglass,
    Image as ImageIcon,
    Landmark,
    Link2,
    ListChecks,
    Mail,
    MapPin,
    MessageSquare,
    Package,
    PauseCircle,
    Pencil,
    Phone,
    PhoneCall,
    Pin,
    PlayCircle,
    Plus,
    Receipt,
    RefreshCw,
    RotateCcw,
    Rows3,
    Send,
    Settings2,
    Sparkles,
    Stethoscope,
    Tag,
    Trash2,
    Undo2,
    Upload,
    User,
    UserPlus,
    Users,
    Wrench,
    X,
} from "lucide-react";
import { JOB_PAGE_TO_CLOSEOUT_LABEL, OFFICE_STATUS_TRANSITIONS, TRANSITION_LABELS } from "@/lib/vocabulary";
import { primaryHeaderActions } from "../jobDetailHelpers";

export type JobActionGroup =
    | "Move to"
    | "This job"
    | "Contacts"
    | "Schedule & crew"
    | "Job details"
    | "Devices"
    | "Paperwork"
    | "Photos"
    | "Money"
    | "Parts & PO"
    | "City & permits"
    | "Notes"
    | "Help";

/** The order groups show in ⌘K and ⋯. */
export const JOB_ACTION_GROUPS: JobActionGroup[] = [
    "Move to",
    "This job",
    "Contacts",
    "Schedule & crew",
    "Job details",
    "Devices",
    "Paperwork",
    "Photos",
    "Money",
    "Parts & PO",
    "City & permits",
    "Notes",
    "Help",
];

/** Every status the office can move a job to (the targets of the shared matrix). */
export const MOVE_TARGETS = [
    "awaiting_scheduling",
    "scheduled",
    "en_route",
    "arrived",
    "in_progress",
    "office_review",
    "cannot_complete",
    "held",
] as const;
export type MoveTarget = (typeof MOVE_TARGETS)[number];
export type MoveActionId = `move:${MoveTarget}`;

const STATIC_IDS = [
    // Move to (besides the plain status moves)
    "approve",
    "send-back",
    "reopen",
    "cancel",
    "open-items",
    "checkup",
    // This job
    "edit-title",
    "copy-number",
    "open-customer",
    "open-site",
    "open-source-estimate",
    "task",
    "edit-job-form",
    "resend-packet",
    "classic-page",
    // Contacts
    "call",
    "manage-contacts",
    "all-contacts",
    // Schedule & crew
    "visit",
    "add-helper",
    "confirm-booking",
    "another-visit",
    "cannot-complete",
    "tech-brief",
    "visit-history",
    // Job details
    "details",
    "tags",
    "helper-value",
    "ai-summary",
    "edit-ask",
    "original-scope",
    "wo-mapping",
    "service-type",
    "more-about",
    // Devices
    "devices",
    "add-device",
    "reconcile",
    "tech-workflow",
    "edit-device",
    "remove-device",
    "visit-kind",
    "record-repair",
    "remove-repair",
    "use-paper",
    "fail-protocol",
    "quoted-devices",
    // Paperwork
    "upload",
    "city-filing",
    "files",
    "all-paperwork",
    "make-official",
    "rename-file",
    "remove-report",
    "delete-document",
    "download-zip",
    "regenerate-reports",
    "wo-pdf",
    "fix-wo-template",
    "regenerate-wo",
    "use-as-completed-wo",
    "preview-file",
    "download-file",
    "packet-history",
    // Photos
    "photos",
    "device-photos",
    // Money
    "money",
    "create-invoice",
    "create-estimate",
    "send-invoice",
    // Parts & PO
    "create-po",
    "parts",
    // City & permits
    "water-authority",
    "price-check",
    "portal",
    "flag-report-input",
    "st-code",
    "permits",
    "registration",
    // Notes
    "note",
    "message",
    "log-call",
    "activity",
    "pinned-note",
    // Help
    "ask-help-desk",
] as const;

export type StaticJobActionId = (typeof STATIC_IDS)[number];
export type JobActionId = StaticJobActionId | MoveActionId;

export const ALL_JOB_ACTION_IDS: JobActionId[] = [
    ...MOVE_TARGETS.map((t) => `move:${t}` as MoveActionId),
    ...STATIC_IDS,
];

/** What the registry needs to know about the job and the person. */
export interface JobActionContext {
    jobId: string;
    status: string;
    /** `vendor_work_order` for Cintas-style jobs. */
    kind?: string | null;
    tradeCode?: string | null;
    customerId?: string | null;
    siteId?: string | null;
    sourceEstimateId?: string | null;
    scheduledStart?: string | null;
    confirmed: boolean;
    hasInvoices: boolean;
    hasUnsentInvoice: boolean;
    deviceCount: number;
    /** Some device on the job has a test that failed. */
    hasFailedDevice: boolean;
    hasRepair: boolean;
    waterAuthorityName?: string | null;
    waterProviderId?: string | null;
    portalUrl?: string | null;
    isPortal: boolean;
    /** The tech workflow link (office, workable, backflow / fire). */
    fieldWorkflowHref: string | null;
    /** The job's trade has a device family, so devices can be linked. */
    tradeHasFamily: boolean;
    /** The vendor WO mapping card has something to show. */
    hasWorkOrderMapping: boolean;
    /** The packet has gone out once (the resend gate, #1545). */
    canResendPacket: boolean;
    perms: {
        isOffice: boolean;
        /** jobs.manage */
        manageJobs: boolean;
        /** office_review.manage: Use the paper, Record repair, Make official, WO tools. */
        manageOfficeReview: boolean;
        /** pricing.view */
        seePricing: boolean;
        /** The "Try the new job page" switch is offered to this person. */
        switchAllowed: boolean;
    };
}

export interface JobActionDef {
    id: JobActionId;
    group: JobActionGroup;
    icon: LucideIcon;
    /** The full words, as in ⌘K and ⋯. */
    label: string;
    /** The short words on a pin. */
    pinLabel: string;
    /** Extra words ⌘K matches on. */
    keywords?: string;
    /** Red in menus (cancel, remove). */
    destructive?: boolean;
    /** Acts on one device or one file; from ⌘K it takes you to the rows. */
    target?: "device" | "file";
    /** The words on a row's own menu ("Edit device" rather than "Edit a device"). */
    rowLabel?: string;
    /** null = applies. A string = the short reason it doesn't, shown greyed. */
    unavailable: (ctx: JobActionContext) => string | null;
}

export interface JobAction extends Omit<JobActionDef, "unavailable"> {
    /** False = greyed, with `reason`. */
    applies: boolean;
    reason: string | null;
    run: () => void;
}

export type JobActionHandlers = Record<JobActionId, () => void>;

const always = () => null;
const officeOnly = (c: JobActionContext) => (c.perms.isOffice ? null : "office only");
const CLOSED = ["completed", "closed", "cancelled"];
const isBackflow = (c: JobActionContext) => c.tradeCode === "backflow";
const isVendorWo = (c: JobActionContext) => c.kind === "vendor_work_order";

/**
 * The status moves, worded the way the classic header words them for THIS
 * status ("Resume" from Hold, "Reschedule" from Couldn't complete, "Reopen"
 * from Completed). Falls back to the shared labels.
 */
export function moveLabel(status: string, target: MoveTarget): string {
    const all = primaryHeaderActions(status, { jobId: "x", hasInvoices: true });
    let label: string | null = null;
    for (const a of [...all.primary, ...all.overflow]) {
        if (a.kind === "status" && a.status === target) {
            label = a.label;
            break;
        }
    }
    label ??= TRANSITION_LABELS[target] ?? target.replace(/_/g, " ");
    // The new page says Closeout, the office's word for office_review (10/9).
    return label === TRANSITION_LABELS.office_review ? JOB_PAGE_TO_CLOSEOUT_LABEL : label;
}

const MOVE_ICON: Record<MoveTarget, LucideIcon> = {
    awaiting_scheduling: Hourglass,
    scheduled: Calendar,
    en_route: Send,
    arrived: MapPin,
    in_progress: PlayCircle,
    office_review: ClipboardCheck,
    cannot_complete: X,
    held: PauseCircle,
};

const MOVE_DEFS: JobActionDef[] = MOVE_TARGETS.map((target) => ({
    id: `move:${target}` as MoveActionId,
    group: "Move to" as const,
    icon: MOVE_ICON[target],
    label: moveLabel("", target),
    pinLabel: moveLabel("", target),
    keywords: `status move ${target.replace(/_/g, " ")}${target === "office_review" ? " closeout" : ""}`,
    unavailable: (c: JobActionContext) =>
        c.status === target
            ? "now"
            : (OFFICE_STATUS_TRANSITIONS[c.status] ?? []).includes(target)
              ? null
              : c.status === "cancelled"
                ? "job is cancelled"
                : "not from here",
}));

const DEFS: JobActionDef[] = [
    ...MOVE_DEFS,
    {
        id: "approve",
        group: "Move to",
        icon: CheckCircle2,
        label: "Review & approve",
        pinLabel: "Approve",
        keywords: "closeout office review approve packet",
        unavailable: (c) => (c.status === "office_review" ? null : "only in Closeout"),
    },
    {
        id: "send-back",
        group: "Move to",
        icon: Undo2,
        label: "Send back to field",
        pinLabel: "Back to field",
        keywords: "return tech redo",
        unavailable: (c) => (c.status === "office_review" ? null : "only in Closeout"),
    },
    {
        id: "reopen",
        group: "Move to",
        icon: RotateCcw,
        label: "Reopen job",
        pinLabel: "Reopen",
        unavailable: (c) => (c.status === "cancelled" ? null : "only when cancelled"),
    },
    {
        id: "cancel",
        group: "Move to",
        icon: Ban,
        label: "Cancel job…",
        pinLabel: "Cancel",
        destructive: true,
        unavailable: (c) => (c.status === "cancelled" ? "now" : null),
    },
    {
        id: "open-items",
        group: "Move to",
        icon: ListChecks,
        label: "See everything still open",
        pinLabel: "Open items",
        keywords: "what needs you blockers next finish line",
        unavailable: always,
    },
    {
        id: "checkup",
        group: "Move to",
        icon: Stethoscope,
        label: "Help Desk check-up: what is in the way",
        pinLabel: "Check-up",
        keywords: "help desk blockers can't go out",
        unavailable: (c) => (c.perms.manageOfficeReview ? null : "Closeout access only"),
    },

    // ── This job ──
    {
        id: "edit-title",
        group: "This job",
        icon: Pencil,
        label: "Edit job title",
        pinLabel: "Title",
        unavailable: always,
    },
    {
        id: "copy-number",
        group: "This job",
        icon: Copy,
        label: "Copy job number",
        pinLabel: "Copy #",
        unavailable: always,
    },
    {
        id: "open-customer",
        group: "This job",
        icon: User,
        label: "Open the customer",
        pinLabel: "Customer",
        unavailable: (c) => (c.customerId ? null : "no customer"),
    },
    {
        id: "open-site",
        group: "This job",
        icon: Building2,
        label: "Open the location",
        pinLabel: "Location",
        keywords: "site address",
        unavailable: (c) => (c.siteId ? null : "no location"),
    },
    {
        id: "open-source-estimate",
        group: "This job",
        icon: Link2,
        label: "Open the estimate this job came from",
        pinLabel: "From estimate",
        keywords: "lineage quote",
        unavailable: (c) => (c.sourceEstimateId ? null : "not from an estimate"),
    },
    {
        id: "task",
        group: "This job",
        icon: ClipboardList,
        label: "Create a task",
        pinLabel: "Task",
        unavailable: always,
    },
    {
        id: "edit-job-form",
        group: "This job",
        icon: FileText,
        label: "Edit the whole job form",
        pinLabel: "Job form",
        keywords: "edit job",
        unavailable: always,
    },
    {
        id: "resend-packet",
        group: "This job",
        icon: RefreshCw,
        label: "Resend packet",
        pinLabel: "Resend packet",
        keywords: "email customer again",
        unavailable: (c) => (c.canResendPacket ? null : "the packet hasn't gone out yet"),
    },
    {
        id: "classic-page",
        group: "This job",
        icon: History,
        label: "Back to the classic job page",
        pinLabel: "Classic page",
        keywords: "old page switch",
        unavailable: (c) => (c.perms.switchAllowed ? null : "not offered"),
    },

    // ── Contacts ──
    { id: "call", group: "Contacts", icon: Phone, label: "Who to call", pinLabel: "Call", unavailable: always },
    {
        id: "manage-contacts",
        group: "Contacts",
        icon: Users,
        label: "Manage contacts",
        pinLabel: "Contacts",
        unavailable: (c) => (c.customerId ? null : "no customer"),
    },
    {
        id: "all-contacts",
        group: "Contacts",
        icon: Users,
        label: "All contacts at this location",
        pinLabel: "All contacts",
        unavailable: (c) => (c.customerId ? null : "no customer"),
    },

    // ── Schedule & crew ──
    {
        id: "visit",
        group: "Schedule & crew",
        icon: Calendar,
        label: "Change date or tech",
        pinLabel: "Visit",
        keywords: "schedule reschedule assign appointment",
        unavailable: always,
    },
    {
        id: "add-helper",
        group: "Schedule & crew",
        icon: UserPlus,
        label: "Add a helper or tech to the crew",
        pinLabel: "Add helper",
        keywords: "crew",
        unavailable: (c) => (CLOSED.includes(c.status) ? "job is finished" : null),
    },
    {
        id: "confirm-booking",
        group: "Schedule & crew",
        icon: BadgeCheck,
        label: "Confirm booking",
        pinLabel: "Confirm",
        keywords: "unconfirm lock appointment",
        unavailable: (c) => (c.scheduledStart ? null : "no appointment yet"),
    },
    {
        id: "another-visit",
        group: "Schedule & crew",
        icon: CalendarPlus,
        label: "Add another visit",
        pinLabel: "Another visit",
        keywords: "multi visit appointment",
        unavailable: (c) => (c.perms.manageJobs ? null : "needs jobs.manage"),
    },
    {
        id: "cannot-complete",
        group: "Schedule & crew",
        icon: X,
        label: "Mark cannot-complete",
        pinLabel: "Can't complete",
        unavailable: (c) =>
            !c.perms.manageJobs
                ? "needs jobs.manage"
                : [...CLOSED, "cannot_complete"].includes(c.status)
                  ? "not from here"
                  : null,
    },
    {
        id: "tech-brief",
        group: "Schedule & crew",
        icon: ClipboardCheck,
        label: "Tech brief and dispatch check",
        pinLabel: "Tech brief",
        unavailable: always,
    },
    {
        id: "visit-history",
        group: "Schedule & crew",
        icon: History,
        label: "Visit history",
        pinLabel: "Visits",
        unavailable: always,
    },

    // ── Job details ──
    {
        id: "details",
        group: "Job details",
        icon: Pencil,
        label: "Job details: type, due date, window, PO, WO",
        pinLabel: "Job details",
        keywords: "due flexibility priority",
        unavailable: always,
    },
    { id: "tags", group: "Job details", icon: Tag, label: "Tags", pinLabel: "Tags", unavailable: always },
    {
        id: "helper-value",
        group: "Job details",
        icon: Gauge,
        label: "Helper value",
        pinLabel: "Helper value",
        unavailable: always,
    },
    {
        id: "ai-summary",
        group: "Job details",
        icon: Sparkles,
        label: "AI summary and estimate lines",
        pinLabel: "AI summary",
        unavailable: always,
    },
    {
        id: "edit-ask",
        group: "Job details",
        icon: MessageSquare,
        label: "The ask (summary)",
        pinLabel: "The ask",
        keywords: "summary scope",
        unavailable: always,
    },
    {
        id: "original-scope",
        group: "Job details",
        icon: FileText,
        label: "View the original scope",
        pinLabel: "Original scope",
        unavailable: (c) => (isVendorWo(c) ? null : "vendor work orders only"),
    },
    {
        id: "wo-mapping",
        group: "Job details",
        icon: Settings2,
        label: "Review / fix the WO PDF mapping",
        pinLabel: "WO mapping",
        unavailable: (c) => (c.hasWorkOrderMapping ? null : "no work order template"),
    },
    {
        id: "service-type",
        group: "Job details",
        icon: Tag,
        label: "Work order service type",
        pinLabel: "Service type",
        unavailable: (c) => (isVendorWo(c) ? null : "vendor work orders only"),
    },
    {
        id: "more-about",
        group: "Job details",
        icon: Rows3,
        label: "Description, replacements, legacy devices, warranties",
        pinLabel: "More details",
        unavailable: always,
    },

    // ── Devices ──
    { id: "devices", group: "Devices", icon: Gauge, label: "Go to devices", pinLabel: "Devices", unavailable: always },
    {
        id: "add-device",
        group: "Devices",
        icon: Plus,
        label: "Add device",
        pinLabel: "Add device",
        unavailable: (c) => (!c.siteId ? "no location" : !c.tradeHasFamily ? "set a trade first" : null),
    },
    {
        id: "reconcile",
        group: "Devices",
        icon: ListChecks,
        label: "Reconcile from list",
        pinLabel: "Reconcile",
        unavailable: (c) => (isBackflow(c) && c.siteId ? null : "backflow jobs with a location"),
    },
    {
        id: "tech-workflow",
        group: "Devices",
        icon: ExternalLink,
        label: "Open the tech workflow",
        pinLabel: "Tech workflow",
        unavailable: (c) => (c.fieldWorkflowHref ? null : "not while finished"),
    },
    {
        id: "edit-device",
        group: "Devices",
        icon: Pencil,
        label: "Edit a device",
        pinLabel: "Edit device",
        rowLabel: "Edit device",
        keywords: "serial make model size location",
        target: "device",
        unavailable: (c) => (c.deviceCount > 0 ? null : "no devices"),
    },
    {
        id: "remove-device",
        group: "Devices",
        icon: X,
        label: "Remove a device from the job",
        pinLabel: "Remove device",
        rowLabel: "Remove from job",
        destructive: true,
        target: "device",
        unavailable: (c) => (c.deviceCount > 0 ? null : "no devices"),
    },
    {
        id: "visit-kind",
        group: "Devices",
        icon: Wrench,
        label: "This visit: Test / Repair / Replace",
        pinLabel: "Visit kind",
        rowLabel: "This visit: Test / Repair / Replace",
        keywords: "scope required action",
        target: "device",
        unavailable: (c) => (!c.perms.isOffice ? "office only" : c.deviceCount > 0 ? null : "no devices"),
    },
    {
        id: "record-repair",
        group: "Devices",
        icon: Wrench,
        label: "Record a repair",
        pinLabel: "Repair",
        rowLabel: "Record repair",
        target: "device",
        unavailable: (c) =>
            !c.perms.manageOfficeReview ? "Closeout access only" : isBackflow(c) ? null : "backflow jobs only",
    },
    {
        id: "remove-repair",
        group: "Devices",
        icon: Undo2,
        label: "Remove a repair",
        pinLabel: "Remove repair",
        rowLabel: "Remove repair",
        destructive: true,
        target: "device",
        unavailable: (c) =>
            !c.perms.manageOfficeReview ? "Closeout access only" : c.hasRepair ? null : "no repair recorded",
    },
    {
        id: "use-paper",
        group: "Devices",
        icon: FileCheck2,
        label: "Use the signed paper",
        pinLabel: "Use paper",
        rowLabel: "Use the signed paper",
        keywords: "paper conversion rip cord",
        target: "device",
        unavailable: (c) =>
            !c.perms.manageOfficeReview ? "Closeout access only" : isBackflow(c) ? null : "backflow jobs only",
    },
    {
        id: "fail-protocol",
        group: "Devices",
        icon: Flag,
        label: "Fail protocol: quote, NTE, decline, return visit",
        pinLabel: "Fail protocol",
        rowLabel: "Fail protocol",
        target: "device",
        unavailable: (c) => (c.hasFailedDevice ? null : "nothing failed"),
    },
    {
        id: "quoted-devices",
        group: "Devices",
        icon: Plus,
        label: "Add the quoted devices",
        pinLabel: "Quoted devices",
        unavailable: (c) =>
            !c.sourceEstimateId ? "not from an estimate" : c.deviceCount > 0 ? "devices already linked" : null,
    },

    // ── Paperwork ──
    {
        id: "upload",
        group: "Paperwork",
        icon: Upload,
        label: "Upload a file",
        pinLabel: "Upload",
        keywords: "attach document report",
        unavailable: always,
    },
    {
        id: "city-filing",
        group: "Paperwork",
        icon: Landmark,
        label: "City filing and cheat sheet",
        pinLabel: "City filing",
        keywords: "submission water authority",
        unavailable: always,
    },
    {
        id: "files",
        group: "Paperwork",
        icon: FolderOpen,
        label: "Office files",
        pinLabel: "Files",
        unavailable: always,
    },
    {
        id: "all-paperwork",
        group: "Paperwork",
        icon: FolderOpen,
        label: "All paperwork",
        pinLabel: "All paperwork",
        keywords: "reports documents",
        unavailable: always,
    },
    {
        id: "make-official",
        group: "Paperwork",
        icon: FileCheck2,
        label: "Make a report official",
        pinLabel: "Make official",
        rowLabel: "Make official",
        target: "file",
        unavailable: (c) => (c.perms.manageOfficeReview ? null : "Closeout access only"),
    },
    {
        id: "rename-file",
        group: "Paperwork",
        icon: Pencil,
        label: "Rename a file",
        pinLabel: "Rename",
        rowLabel: "Rename",
        target: "file",
        unavailable: always,
    },
    {
        id: "remove-report",
        group: "Paperwork",
        icon: FileX2,
        label: "Remove a report from the job",
        pinLabel: "Remove report",
        rowLabel: "Remove report",
        destructive: true,
        target: "file",
        unavailable: always,
    },
    {
        id: "delete-document",
        group: "Paperwork",
        icon: Trash2,
        label: "Delete a document",
        pinLabel: "Delete document",
        rowLabel: "Delete document",
        destructive: true,
        target: "file",
        unavailable: always,
    },
    {
        id: "download-zip",
        group: "Paperwork",
        icon: Download,
        label: "Download files as a zip",
        pinLabel: "Zip",
        unavailable: always,
    },
    {
        id: "regenerate-reports",
        group: "Paperwork",
        icon: RefreshCw,
        label: "Regenerate the reports",
        pinLabel: "Regenerate",
        unavailable: always,
    },
    {
        id: "wo-pdf",
        group: "Paperwork",
        icon: Receipt,
        label: "Open the work order PDF",
        pinLabel: "WO PDF",
        unavailable: (c) => (isVendorWo(c) ? null : "vendor work orders only"),
    },
    {
        id: "fix-wo-template",
        group: "Paperwork",
        icon: Settings2,
        label: "Fix the WO template",
        pinLabel: "Fix template",
        unavailable: (c) =>
            !isVendorWo(c) ? "vendor work orders only" : c.perms.manageOfficeReview ? null : "Closeout access only",
    },
    {
        id: "regenerate-wo",
        group: "Paperwork",
        icon: RefreshCw,
        label: "Regenerate the WO PDF",
        pinLabel: "Regen WO",
        unavailable: (c) =>
            !isVendorWo(c) ? "vendor work orders only" : c.perms.manageOfficeReview ? null : "Closeout access only",
    },
    {
        id: "use-as-completed-wo",
        group: "Paperwork",
        icon: CheckCircle2,
        label: "Use a file as the completed WO",
        pinLabel: "Completed WO",
        rowLabel: "Use as completed WO",
        keywords: "held packet release",
        target: "file",
        unavailable: (c) =>
            !isVendorWo(c) ? "vendor work orders only" : c.perms.manageOfficeReview ? null : "Closeout access only",
    },
    {
        id: "preview-file",
        group: "Paperwork",
        icon: Eye,
        label: "Preview a file",
        pinLabel: "Preview",
        rowLabel: "Preview",
        target: "file",
        unavailable: always,
    },
    {
        id: "download-file",
        group: "Paperwork",
        icon: Download,
        label: "Download a file",
        pinLabel: "Download",
        rowLabel: "Download",
        target: "file",
        unavailable: always,
    },
    {
        id: "packet-history",
        group: "Paperwork",
        icon: Mail,
        label: "Packet sent: who got it, view the email",
        pinLabel: "Packet",
        keywords: "send history delivered bounced",
        unavailable: always,
    },

    // ── Photos ──
    { id: "photos", group: "Photos", icon: Camera, label: "Job photos", pinLabel: "Photos", unavailable: always },
    {
        id: "device-photos",
        group: "Photos",
        icon: ImageIcon,
        label: "Device photos",
        pinLabel: "Device photos",
        rowLabel: "See photos",
        target: "device",
        unavailable: (c) => (c.deviceCount > 0 ? null : "no devices"),
    },

    // ── Money ──
    {
        id: "money",
        group: "Money",
        icon: DollarSign,
        label: "Invoices and estimates",
        pinLabel: "Money",
        unavailable: always,
    },
    {
        id: "create-invoice",
        group: "Money",
        icon: Receipt,
        label: "Create invoice",
        pinLabel: "+ Invoice",
        unavailable: (c) => (c.hasInvoices ? "this job already has an invoice" : null),
    },
    {
        id: "create-estimate",
        group: "Money",
        icon: Calculator,
        label: "Create estimate",
        pinLabel: "+ Estimate",
        keywords: "quote",
        unavailable: always,
    },
    {
        id: "send-invoice",
        group: "Money",
        icon: Send,
        label: "Send the invoice",
        pinLabel: "Send invoice",
        unavailable: (c) => (c.hasUnsentInvoice ? null : "no unsent invoice"),
    },

    // ── Parts & PO ──
    {
        id: "create-po",
        group: "Parts & PO",
        icon: Package,
        label: "Create purchase order",
        pinLabel: "Create PO",
        unavailable: officeOnly,
    },
    {
        id: "parts",
        group: "Parts & PO",
        icon: Link2,
        label: "Link, stage or check in a part",
        pinLabel: "Parts",
        unavailable: always,
    },

    // ── City & permits ──
    {
        id: "water-authority",
        group: "City & permits",
        icon: Pencil,
        label: "Edit the water authority and account",
        pinLabel: "Water authority",
        unavailable: (c) => (c.siteId ? null : "no location"),
    },
    {
        id: "price-check",
        group: "City & permits",
        icon: DollarSign,
        label: "Price check",
        pinLabel: "Price check",
        unavailable: (c) => (!c.perms.seePricing ? "pricing only" : c.waterProviderId ? null : "no water authority"),
    },
    {
        id: "portal",
        group: "City & permits",
        icon: ExternalLink,
        label: "Open the city portal",
        pinLabel: "Portal",
        unavailable: (c) => (c.portalUrl ? null : "no portal"),
    },
    {
        id: "flag-report-input",
        group: "City & permits",
        icon: Hourglass,
        label: "Flag: waiting on the tester or a city hold",
        pinLabel: "Flag",
        unavailable: (c) => (c.isPortal ? null : "portal cities only"),
    },
    {
        id: "st-code",
        group: "City & permits",
        icon: Hash,
        label: "Filing method and ST code",
        pinLabel: "Filing method",
        unavailable: always,
    },
    {
        id: "permits",
        group: "City & permits",
        icon: Hash,
        label: "Permit numbers",
        pinLabel: "Permits",
        unavailable: always,
    },
    {
        id: "registration",
        group: "City & permits",
        icon: BadgeCheck,
        label: "Tester registration",
        pinLabel: "Registration",
        unavailable: officeOnly,
    },

    // ── Notes ──
    { id: "note", group: "Notes", icon: Plus, label: "Add a note", pinLabel: "Note", unavailable: always },
    {
        id: "message",
        group: "Notes",
        icon: Mail,
        label: "Message the customer",
        pinLabel: "Message",
        unavailable: always,
    },
    { id: "log-call", group: "Notes", icon: PhoneCall, label: "Log a call", pinLabel: "Log call", unavailable: always },
    {
        id: "activity",
        group: "Notes",
        icon: Rows3,
        label: "Activity: pin or edit notes",
        pinLabel: "Activity",
        keywords: "history feed",
        unavailable: always,
    },
    {
        id: "pinned-note",
        group: "Notes",
        icon: Pin,
        label: "Pinned note (techs and PDFs see it)",
        pinLabel: "Pinned note",
        keywords: "tech instructions",
        unavailable: always,
    },

    // ── Help ──
    {
        id: "ask-help-desk",
        group: "Help",
        icon: HelpCircle,
        label: "Ask the Help Desk about this job",
        pinLabel: "Help Desk",
        unavailable: always,
    },
];

/** Every action's fixed facts, by id. */
export const JOB_ACTION_DEFS: ReadonlyMap<JobActionId, JobActionDef> = new Map(DEFS.map((d) => [d.id, d]));

/**
 * Everything you can do on this job, in group order. Actions that don't
 * apply are still in the list (greyed, with the reason), so nothing seems
 * to vanish; callers that only want live ones filter on `applies`.
 */
export function jobActions(ctx: JobActionContext, handlers: JobActionHandlers): JobAction[] {
    return DEFS.map(({ unavailable, ...d }) => {
        const reason = unavailable(ctx);
        const label = d.id.startsWith("move:") ? moveLabel(ctx.status, d.id.slice(5) as MoveTarget) : d.label;
        return {
            ...d,
            label,
            pinLabel: d.id.startsWith("move:") ? label : d.pinLabel,
            applies: reason === null,
            reason,
            run: handlers[d.id],
        };
    }).sort((a, b) => JOB_ACTION_GROUPS.indexOf(a.group) - JOB_ACTION_GROUPS.indexOf(b.group));
}

/** ⌘K's match: label, group and keywords, every word. */
export function matchesJobAction(a: Pick<JobAction, "label" | "group" | "keywords" | "pinLabel">, q: string): boolean {
    const hay = `${a.label} ${a.group} ${a.pinLabel} ${a.keywords ?? ""}`.toLowerCase();
    return q
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .every((w) => hay.includes(w));
}

/**
 * The office's starting pins (Corbin, 2026-10-07/08): the five most-used
 * classic actions in the 30-day counts. Everyone starts here; each person can
 * change theirs, and "Reset to office default" comes back here.
 */
export const OFFICE_DEFAULT_PINS: JobActionId[] = [
    "details",
    "city-filing",
    "add-helper",
    "resend-packet",
    "add-device",
];

/** The per-user pins preference key (server-side, user_preferences). */
export const JOB_PINS_PREF_KEY = "job-page.pins";

/** Keep only known, unique ids; null/garbage = the office default. */
export function normalizeJobPins(value: unknown): JobActionId[] | null {
    if (!Array.isArray(value)) return null;
    const known = new Set<string>(ALL_JOB_ACTION_IDS);
    const out: JobActionId[] = [];
    for (const v of value) {
        if (typeof v === "string" && known.has(v) && !out.includes(v as JobActionId)) out.push(v as JobActionId);
    }
    return out.slice(0, 12);
}

/** Pin or unpin one action. */
export function togglePin(pins: JobActionId[], id: JobActionId): JobActionId[] {
    return pins.includes(id) ? pins.filter((p) => p !== id) : [...pins, id];
}

export function sameAsOfficeDefault(pins: JobActionId[]): boolean {
    return pins.length === OFFICE_DEFAULT_PINS.length && pins.every((p, i) => p === OFFICE_DEFAULT_PINS[i]);
}

// ── Row menus (job page v2, slice 3) ────────────────────────────────────────

/** What a device row's menu offers, in order. */
export const DEVICE_ROW_ACTION_IDS = [
    "edit-device",
    "record-repair",
    "remove-repair",
    "use-paper",
    "visit-kind",
    "device-photos",
    "fail-protocol",
    "remove-device",
] as const satisfies readonly JobActionId[];
export type DeviceRowActionId = (typeof DEVICE_ROW_ACTION_IDS)[number];

/** Facts about one device row that decide its menu. */
export interface DeviceRowFacts {
    failed: boolean;
    /** The existing "Record repair" applies (repair-scoped, none saved yet). */
    canRecordRepair: boolean;
    hasRepair: boolean;
    /** The existing "Use the paper" applies to this row. */
    canUsePaper: boolean;
    /** Test / Repair / Replace can still change (nothing recorded yet). */
    canScope: boolean;
    hasPhotos: boolean;
    hasProtocol: boolean;
    canRemove: boolean;
}

/** What a file row's menu offers, in order. */
export const FILE_ROW_ACTION_IDS = [
    "preview-file",
    "make-official",
    "rename-file",
    "download-file",
    "use-as-completed-wo",
    "remove-report",
    "delete-document",
] as const satisfies readonly JobActionId[];
export type FileRowActionId = (typeof FILE_ROW_ACTION_IDS)[number];

/** Facts about one file row that decide its menu. */
export interface FileRowFacts {
    canMakeOfficial: boolean;
    canRename: boolean;
    canUseAsWorkOrder: boolean;
    /** A test report: removed from the job, kept on file. */
    isReport: boolean;
    /** An office document: deleted. */
    isDocument: boolean;
}

function rowAction(id: JobActionId, reason: string | null, run: (() => void) | undefined): JobAction | null {
    const d = JOB_ACTION_DEFS.get(id);
    if (!d || !run) return null;
    const { unavailable: _unavailable, ...rest } = d;
    return { ...rest, label: d.rowLabel ?? d.label, applies: reason === null, reason, run };
}

/**
 * One device row's actions, worded for the row, from the same list. A row
 * only offers what the page can run for it (`run` missing = not offered).
 */
export function deviceRowActions(
    facts: DeviceRowFacts,
    run: Partial<Record<DeviceRowActionId, () => void>>,
): JobAction[] {
    const reason: Record<DeviceRowActionId, string | null> = {
        "edit-device": null,
        "record-repair": facts.canRecordRepair ? null : facts.hasRepair ? "repair already saved" : "not set to Repair",
        "remove-repair": facts.hasRepair ? null : "no repair recorded",
        "use-paper": facts.canUsePaper ? null : "not for this device",
        "visit-kind": facts.canScope ? null : "already recorded",
        "device-photos": facts.hasPhotos ? null : "no photos",
        "fail-protocol": facts.hasProtocol ? null : "nothing failed",
        "remove-device": facts.canRemove ? null : "not now",
    };
    return DEVICE_ROW_ACTION_IDS.map((id) => rowAction(id, reason[id], run[id])).filter(
        (a): a is JobAction => a !== null,
    );
}

/** One file row's actions, worded for the row, from the same list. */
export function fileRowActions(facts: FileRowFacts, run: Partial<Record<FileRowActionId, () => void>>): JobAction[] {
    const reason: Record<FileRowActionId, string | null> = {
        "preview-file": null,
        "make-official": facts.canMakeOfficial ? null : "not a report candidate",
        "rename-file": facts.canRename ? null : "can't rename this kind",
        "download-file": null,
        "use-as-completed-wo": facts.canUseAsWorkOrder ? null : "not for this file",
        "remove-report": facts.isReport ? null : "not a report",
        "delete-document": facts.isDocument ? null : "not an office document",
    };
    return FILE_ROW_ACTION_IDS.map((id) => rowAction(id, reason[id], run[id])).filter(
        (a): a is JobAction => a !== null && (a.applies || a.id === "preview-file" || a.id === "download-file"),
    );
}
